// Super Admin ▸ Messaging safety — the pure decisions behind the panel (2026-10-07).
//
// Tests: cypress/node-tests/whatsapp-only-admin-controls.test.js (imports this file).

import { extractApiError } from './apiError.js';

/**
 * Did a superadmin/sendAction call actually succeed?
 *
 * ⛔ sendAction NEVER throws on an HTTP error: it catches the axios rejection and RETURNS
 * `{ status, data }` (src/store/superadmin.js). So `try { await sendAction(); toast.success() }`
 * reports success for every refusal. Read the status instead — and treat the older
 * 200 `{ success:false, error }` answers as refusals too.
 *
 * @param {{status?: number, data?: any}|null|undefined} res   what sendAction returned
 * @param {string} fallback  copy when the server gave nothing presentable
 * @returns {{ ok: boolean, message: string|null }}
 */
export function actionOutcome(res, fallback) {
  const status = res && res.status;
  const data = res && res.data;
  const ok = typeof status === 'number' && status >= 200 && status < 300 && !(data && data.success === false);
  if (ok) return { ok: true, message: null };
  // extractApiError reads a bare-string body, { error } or { message } — every shape the
  // admin Lambda answers with — and withholds HTML pages / internal validation text.
  return { ok: false, message: extractApiError({ response: { status, data } }, fallback) };
}

/**
 * The per-site "WhatsApp-only" control (outbound SMS off, SMS_OUTBOUND_DISABLED) for one row of
 * GET_KILL_STATUS. Mirrors teamio-admin setSiteSmsOutbound: turning texting OFF needs WhatsApp
 * on for that practice (else it could message no one); turning it back ON is never blocked.
 *
 * @param {{ smsOutbound?: boolean, whatsappEnabled?: boolean }} site
 * @returns {{ on: boolean, canToggle: boolean, disabledReason: string|null, warning: string|null }}
 */
export function smsOutboundControl(site) {
  const on = !!(site && site.smsOutbound === true);
  const whatsappEnabled = !!(site && site.whatsappEnabled === true);
  if (on) {
    return {
      on,
      canToggle: true,
      disabledReason: null,
      // Degenerate: the admin refuses to create it, but WhatsApp can be switched off elsewhere
      // (or by hand). Say plainly what it means for the office.
      warning: whatsappEnabled ? null : "Texting is off and WhatsApp is off — this office can't message patients. Turn texting back on, or turn WhatsApp on.",
    };
  }
  return {
    on,
    canToggle: whatsappEnabled,
    disabledReason: whatsappEnabled ? null : 'Turn WhatsApp on for this practice first.',
    warning: null,
  };
}

/** The request for the per-site control. Per-site only: there is deliberately no global form. */
export function smsOutboundPayload(site, on, reason) {
  return { action: 'SET_SITE_SMS_OUTBOUND', siteID: site && site.siteID, on: on === true, reason: reason || null };
}
